import { FC, createContext, useContext, useEffect, useState } from 'react'
import Cookies from 'universal-cookie'
import { loadingProfileAction, ProfileInterface } from '@state/profile/profileStateSlice'
import ProfileService from '../services/profile.service'
import { toast } from 'react-hot-toast'
import { useDispatch } from 'react-redux'
import keycloak from '../keycloak'

interface IAuthProvider {
  handleLogin: () => void
  handleLogout: () => void
  getMemberProfile: () => ProfileInterface | null
  isAuthenticated: boolean
}

const AuthContext = createContext<IAuthProvider>({
  handleLogin: () => {
    return
  },
  handleLogout: () => {
    return
  },
  getMemberProfile: () => null,
  isAuthenticated: false,
})

interface IAuthProviderProps {
  children?: any
}

const AuthProvider: FC<IAuthProviderProps> = ({ children }) => {
  const cookie = new Cookies()
  const profileService = new ProfileService()
  const dispatch = useDispatch()

  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [memberProfile, setMemberProfile] = useState<ProfileInterface | null>(null)

  useEffect(() => {
    keycloak
      .init({ onLoad: 'check-sso', silentCheckSsoRedirectUri: window.location.origin + '/silent-check-sso.html' })
      .then((authenticated: boolean) => {
        if (authenticated) {
          cookie.set('access-token', keycloak.token, { path: '/' })
          window.sessionStorage.setItem('logged', 'true')
          setIsAuthenticated(true)

          profileService
            .createUserProfile()
            .then(data => {
              setMemberProfile(data)
              dispatch(loadingProfileAction())
            })
            .catch(error => {
              if (error?.response?.status !== 401) {
                toast.error(error?.response?.data?.message)
              }
            })
        } else {
          cookie.remove('access-token')
          window.sessionStorage.removeItem('logged')
          setIsAuthenticated(false)
        }
      })
      .catch(console.error)
  }, [])

  const handleLogin = () => {
    keycloak.login()
  }

  const handleLogout = () => {
    cookie.remove('access-token')
    window.sessionStorage.removeItem('logged')
    keycloak.logout({ redirectUri: window.location.origin })
  }

  const getMemberProfile = () => memberProfile

  const value = { handleLogin, handleLogout, getMemberProfile, isAuthenticated }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

const useAuth = () => {
  return useContext(AuthContext)
}

export { useAuth, AuthProvider }
